import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LogOut, RefreshCw, Trash2, Search, Phone, Mail, User, Calendar, MessageSquare } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Login({ onLogin }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('rp_admin_token', res.data.token);
      onLogin();
    } catch (err) {
      toast({ title: 'Login failed', description: 'Invalid password. Try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b3d5b] via-[#0d4a6f] to-[#082e46] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-xl shadow">RP</div>
          <div>
            <div className="font-extrabold text-[#0b3d5b] text-xl">Register Point</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Admin Panel</div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-[#0b3d5b]">Sign in</h1>
        <p className="text-sm text-slate-500 mt-1">Enter your admin password to continue.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input
            type="password"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
            autoFocus
          />
          <button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-70 text-white font-semibold py-3 rounded-lg transition-colors">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-2 text-3xl font-extrabold ${color}`}>{value}</div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const { toast } = useToast();

  const token = () => localStorage.getItem('rp_admin_token');
  const headers = () => ({ 'X-Admin-Token': token() });

  const load = async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, { headers: headers() }),
        axios.get(`${API}/admin/stats`, { headers: headers() }),
      ]);
      setEnquiries(e.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        toast({ title: 'Failed to load', description: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, { headers: headers() });
      toast({ title: 'Status updated' });
      load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (err) {
      toast({ title: 'Failed', description: err.message });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, { headers: headers() });
      toast({ title: 'Deleted' });
      setSelected(null);
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err.message });
    }
  };

  const filtered = enquiries
    .filter((e) => filter === 'all' || e.status === filter)
    .filter((e) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return e.name?.toLowerCase().includes(q) || e.mobile?.includes(q) || e.email?.toLowerCase().includes(q) || e.service?.toLowerCase().includes(q);
    });

  const statusColors = {
    new: 'bg-orange-100 text-orange-700 border-orange-200',
    contacted: 'bg-blue-100 text-blue-700 border-blue-200',
    closed: 'bg-green-100 text-green-700 border-green-200',
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold shadow">RP</div>
            <div>
              <div className="font-extrabold text-[#0b3d5b]">Register Point Admin</div>
              <div className="text-xs text-slate-500">Enquiry Management</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm flex items-center gap-1.5 transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={onLogout} className="px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-sm flex items-center gap-1.5 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total" value={stats.total} color="text-[#0b3d5b]" />
          <StatCard label="New" value={stats.new} color="text-orange-600" />
          <StatCard label="Contacted" value={stats.contacted} color="text-blue-600" />
          <StatCard label="Closed" value={stats.closed} color="text-green-600" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, email, service…"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 outline-none text-sm"
            />
          </div>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            {['all', 'new', 'contacted', 'closed'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${filter === f ? 'bg-white text-[#0b3d5b] shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
              >{f}</button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">Service</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-12 text-slate-500">No enquiries yet.</td></tr>
                ) : filtered.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-medium text-[#0b3d5b]">{e.name}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="flex items-center gap-1.5 text-xs"><Phone className="w-3 h-3" /> {e.mobile}</div>
                      {e.email && <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5"><Mail className="w-3 h-3" /> {e.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{e.service || e.subject || '-'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <select value={e.status} onChange={(ev) => updateStatus(e.id, ev.target.value)} className={`text-xs font-semibold rounded-full px-3 py-1 border ${statusColors[e.status]} outline-none cursor-pointer`}>
                        <option value="new">new</option>
                        <option value="contacted">contacted</option>
                        <option value="closed">closed</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setSelected(e)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="View"><MessageSquare className="w-4 h-4" /></button>
                        <button onClick={() => remove(e.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#0b3d5b] text-xl">Enquiry Details</h3>
              <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-slate-900">✕</button>
            </div>
            <div className="space-y-3 text-sm">
              <Row icon={User} label="Name" value={selected.name} />
              <Row icon={Phone} label="Mobile" value={selected.mobile} />
              <Row icon={Mail} label="Email" value={selected.email || '-'} />
              <Row icon={MessageSquare} label="Service" value={selected.service || selected.subject || '-'} />
              <Row icon={Calendar} label="Received" value={new Date(selected.created_at).toLocaleString()} />
              {selected.message && (
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Message</div>
                  <div className="bg-slate-50 p-3 rounded-lg text-slate-700">{selected.message}</div>
                </div>
              )}
              <div className="flex items-center gap-2 pt-2">
                <span className="text-xs text-slate-500">Status:</span>
                <span className={`text-xs font-semibold rounded-full px-3 py-1 border ${statusColors[selected.status]}`}>{selected.status}</span>
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <a href={`tel:${selected.mobile}`} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 rounded-lg text-sm text-center">Call now</a>
              {selected.email && <a href={`mailto:${selected.email}`} className="flex-1 bg-[#0b3d5b] hover:bg-[#0d4a6f] text-white font-semibold py-2 rounded-lg text-sm text-center">Email</a>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
      <div>
        <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
        <div className="text-slate-800">{value}</div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [authed, setAuthed] = useState(!!localStorage.getItem('rp_admin_token'));

  const logout = () => {
    localStorage.removeItem('rp_admin_token');
    setAuthed(false);
  };

  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
